import React, { ReactNode } from "react";
import { IconButton, Typography, Box, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material";
import { useTheme } from "@mui/material";
import isPropValid from "@emotion/is-prop-valid";
import { useMandatorContext } from "@bn/theming";
import CloseIcon from "@icons/myicons/close-thin.svg?react";
import { Logo } from "@components/Logo";
import { BankAvatar } from "@components";

const SCREEN_BREAKPOINT = 720;

const Container = styled(Box, {
  shouldForwardProp: (prop: string) =>
    isPropValid(prop) || prop === "boxShadow" || prop === "justifyContent",
})`
  background-color: ${({ theme }) =>
    theme.bankConnection?.headerBackground ??
    theme.palette.wizardHeader.header};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: ${({ theme }) => theme.spacing(2, 4)};
  position: fixed;
  z-index: 1000;
  top: ${({ theme }) => theme.spacing(4)};
  left: ${({ theme }) => theme.spacing(4)};
  width: calc(100% - ${({ theme }) => theme.spacing(8)});
  border-radius: ${({ theme }) => theme.shape.borderRadius * 1.2}px;
  box-shadow: ${({ theme }) => theme.shadows[1]};

  ${({ theme }) => theme.breakpoints.down(SCREEN_BREAKPOINT)} {
    padding: ${({ theme }) => theme.spacing(2, 3)};
  }
`;

const Title = styled(Typography, {
  shouldForwardProp: (name) => name !== "textColor",
})`
  ${({ textColor }: { textColor?: string; component: string }) =>
    textColor && `color: ${textColor};`}
  position: absolute;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ theme }) => theme.breakpoints.down("sm")} {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    font-size: ${({ theme }) => theme.typography.body3.fontSize};
  }
`;

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: "50%",
  width: "32px",
  height: "32px",
  color: theme.palette.wizardHeader.closeBtnIconDefault,
  border: `1px solid ${theme.palette.wizardHeader.closeBtnBorder}`,

  "&:hover": {
    color: theme.palette.wizardHeader.closeBtnIconHover,
    backgroundColor: "transparent",
  },
}));

export interface HeaderProps {
  backUrl: string;
  bankLogoUrl?: string;
  title?: ReactNode;
}

export const BankImportHeader = ({
  backUrl,
  title,
  bankLogoUrl,
}: HeaderProps) => {
  const theme = useTheme();
  const color = theme.palette.wizardHeader.textOnHeader;
  const isLessMd = useMediaQuery(theme.breakpoints.down(SCREEN_BREAKPOINT));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { isAriva } = useMandatorContext();

  // On Ariva desktop the standalone header sits on top of this flow, so the
  // full in-app header would be a duplicate. Keep only a close button
  // (top-right) so the user can still leave the flow. On mobile the Ariva
  // header isn't shown, so keep the full header for navigation.
  if (isAriva && !isXs) {
    return (
      <Box display="flex" justifyContent="flex-end" px={4} pt={4}>
        <IconButton
          onClick={() => {
            window.location.href = backUrl;
          }}
          size="small"
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <CloseIcon width={13} height={13} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Container>
      <Box>
        <Logo variant={isLessMd ? "superCompact" : "compact"} />
      </Box>
      <Box display="flex" alignItems="center" gap={3}>
        {(title || bankLogoUrl) && (
          <Title textColor={color} component="div">
            {isLessMd && bankLogoUrl && (
              <Box
                display="flex"
                width="30px"
                height="30px"
                sx={{
                  boxSizing: "border-box",
                  border: `1px solid ${theme.palette.border.primary}`,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <BankAvatar imgUrl={bankLogoUrl} size={30} />
              </Box>
            )}
            {title}
          </Title>
        )}
      </Box>

      <StyledIconButton
        onClick={() => {
          window.location.href = backUrl;
        }}
      >
        <CloseIcon width={13} height={13} />
      </StyledIconButton>
    </Container>
  );
};
